"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getSession = getSession;
const jwt_decode_1 = require("jwt-decode");
const react_1 = require("next-auth/react");
const get_cached_session_store_1 = require("./get-cached-session-store");
const fetch_retry_1 = require("./fetch-retry");
const constants_1 = require("../../constants");
const handle_token_refresh_error_1 = require("./handle-token-refresh-error");
const RETRY_LIMIT = 3;
const INITIAL_BACKOFF = 500;
const fetchSession = async (params) => {
    try {
        const { allowExpiredToken } = params ?? {};
        const session = await (0, react_1.getSession)(params);
        if (session && (0, handle_token_refresh_error_1.hasRefreshTokenError)(session)) {
            return (0, handle_token_refresh_error_1.handleTokenRefreshError)(session, allowExpiredToken);
        }
        return session;
    }
    catch (error) {
        // eslint-disable-next-line no-console
        console.warn(error);
        return null;
    }
};
const fetchSessionWithRetry = async (params) => {
    return (0, fetch_retry_1.fetchRetry)(() => fetchSession(params), {
        onFailedAttempt: (error, attemptNumber, retriesLeft) => {
            // eslint-disable-next-line no-console
            console.error(error);
            // eslint-disable-next-line no-console
            console.log(`Attempt ${attemptNumber} failed. There are ${retriesLeft} retries left.`);
        },
        retries: RETRY_LIMIT,
        minTimeout: INITIAL_BACKOFF,
    });
};
const safelyParseTokensExpiry = (token) => {
    try {
        const { exp } = (0, jwt_decode_1.jwtDecode)(token);
        return exp;
    }
    catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Error parsing token expiry', err);
        return 0;
    }
};
/**
 * Caches the session until it's about to expire in memory.
 * If the session is about to expire, it will attempt to refresh it.
 * @param opts - Options for getCachedSession
 * @param opts.bypassCache - Bypasses the cache and fetches a new session
 * @returns A promise that resolves to the session
 */
async function getSession(opts) {
    const { cachedSession } = get_cached_session_store_1.cachedSessionStore;
    if (!opts?.bypassCache && cachedSession?.accessToken) {
        const exp = safelyParseTokensExpiry(cachedSession.accessToken);
        // Check if token is expired (exp is in seconds so it is converted to ms)
        const isTokenExpired = exp * 1000 - Date.now() < constants_1.TOKEN_REFRESH_BUFFER_MS;
        if (!isTokenExpired) {
            return cachedSession;
        }
    }
    if (!get_cached_session_store_1.cachedSessionStore.fetchPromise) {
        get_cached_session_store_1.cachedSessionStore.fetchPromise = fetchSessionWithRetry(opts)
            .then((session) => {
            if (session?.user) {
                get_cached_session_store_1.cachedSessionStore.cachedSession = { ...session };
            }
            return session;
        })
            .finally(() => {
            get_cached_session_store_1.cachedSessionStore.fetchPromise = null;
        });
    }
    return get_cached_session_store_1.cachedSessionStore.fetchPromise;
}
